import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import { analyseRound, MONTHS } from '../lib/analysis'
import '../styles/History.css'

export default function History() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [rounds, setRounds] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.from('rounds').select('*').eq('user_id', user.id).order('played_at', { ascending: false }).then(({ data }) => {
      setRounds(data || [])
      setLoading(false)
    })
  }, [])

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>

  if (rounds.length === 0) return (
    <div>
      <div className="history-header"><h2>Round History</h2><p>Your improvement over time</p></div>
      <div className="empty-state">
        <div className="big-icon">📈</div>
        <h3>No Data Yet</h3>
        <p>Log some rounds to see your improvement trends</p>
        <button className="btn-orange" onClick={() => navigate('/log')}>Log a Round</button>
      </div>
    </div>
  )

  const sorted = [...rounds].reverse()
  const maxScore = Math.max(...sorted.map(r => r.score))
  const minScore = Math.min(...sorted.map(r => r.score))
  const range = maxScore - minScore || 1

  const scoreDiff = (r) => { const d = r.score - r.par; return d > 0 ? `+${d}` : d === 0 ? 'E' : `${d}` }
  const scoreColor = (r) => { const d = r.score - r.par; return d > 5 ? 'var(--red)' : d <= 0 ? 'var(--green)' : 'var(--white)' }

  // Averages across all rounds
  const avg = (fn) => Math.round(rounds.reduce((s, r) => s + fn(r), 0) / rounds.length * 10) / 10
  const avgScore = avg(r => r.score)
  const avgFIR = Math.round(rounds.reduce((s, r) => s + (r.fw / (r.fw_total || 14)) * 100, 0) / rounds.length)
  const avgGIR = Math.round(rounds.reduce((s, r) => s + (r.gir / (r.holes || 18)) * 100, 0) / rounds.length)
  const avgPutts = avg(r => r.putts)
  const avgThreePutts = avg(r => r.three_putts || 0)
  const avgOnePutts = avg(r => r.one_putts || 0)
  const avgUD = Math.round(rounds.filter(r => r.ud_att > 0).reduce((s, r) => s + (r.ud_made / r.ud_att) * 100, 0) / rounds.filter(r => r.ud_att > 0).length || 0)
  const avgSandSave = Math.round(rounds.filter(r => r.bunkers > 0).reduce((s, r) => s + (r.bunker_saves / r.bunkers) * 100, 0) / (rounds.filter(r => r.bunkers > 0).length || 1))

  return (
    <div className="history">
      <div className="history-header">
        <h2>Round History</h2>
        <p>{rounds.length} round{rounds.length > 1 ? 's' : ''} logged</p>
      </div>

      {/* Overall averages */}
      <div className="section-title">Season Averages</div>
      <div className="averages-grid">
        <div className="avg-card">
          <div className="avg-value orange">{avgScore}</div>
          <div className="avg-label">Avg Score</div>
        </div>
        <div className="avg-card">
          <div className="avg-value">{avgFIR}%</div>
          <div className="avg-label">FIR</div>
        </div>
        <div className="avg-card">
          <div className="avg-value">{avgGIR}%</div>
          <div className="avg-label">GIR</div>
        </div>
        <div className="avg-card">
          <div className="avg-value">{avgPutts}</div>
          <div className="avg-label">Avg Putts</div>
        </div>
        <div className="avg-card">
          <div className="avg-value">{avgOnePutts}</div>
          <div className="avg-label">Avg 1-Putts</div>
        </div>
        <div className="avg-card">
          <div className={`avg-value ${avgThreePutts >= 3 ? 'red' : avgThreePutts >= 2 ? 'yellow' : 'green'}`}>{avgThreePutts}</div>
          <div className="avg-label">Avg 3-Putts</div>
        </div>
        <div className="avg-card">
          <div className={`avg-value ${avgUD >= 50 ? 'green' : avgUD >= 35 ? 'yellow' : 'red'}`}>{avgUD}%</div>
          <div className="avg-label">Up & Down %</div>
        </div>
        <div className="avg-card">
          <div className={`avg-value ${avgSandSave >= 50 ? 'green' : avgSandSave >= 30 ? 'yellow' : 'red'}`}>{avgSandSave}%</div>
          <div className="avg-label">Sand Save %</div>
        </div>
      </div>

      {/* Score trend */}
      <div className="trend-card">
        <div className="section-title">Score Trend</div>
        <div className="trend-chart">
          {sorted.map((r, i) => {
            const h = Math.round(30 + ((r.score - minScore) / range) * 50)
            const isLatest = i === sorted.length - 1
            const d = new Date(r.played_at)
            return (
              <div className="trend-bar-wrap" key={r.id} onClick={() => navigate(`/results/${r.id}`)}>
                <div className="trend-score-label">{r.score}</div>
                <div className={`trend-bar ${isLatest ? 'latest' : ''}`} style={{ height: h }} />
                <div className="trend-bar-label">{MONTHS[d.getMonth()]}</div>
              </div>
            )
          })}
        </div>
      </div>

      {/* GIR trend */}
      <div className="trend-card">
        <div className="section-title">GIR Trend</div>
        <div className="trend-chart">
          {sorted.map((r, i) => {
            const holes = r.holes || 18
            const h = Math.round(20 + (r.gir / holes) * 60)
            const isLatest = i === sorted.length - 1
            const d = new Date(r.played_at)
            return (
              <div className="trend-bar-wrap" key={r.id}>
                <div className="trend-score-label">{r.gir}</div>
                <div className={`trend-bar ${isLatest ? 'latest' : ''}`} style={{ height: h }} />
                <div className="trend-bar-label">{MONTHS[d.getMonth()]}</div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Putts trend */}
      <div className="trend-card">
        <div className="section-title">Putting Trend</div>
        <div className="trend-chart">
          {sorted.map((r, i) => {
            const maxP = Math.max(...sorted.map(x => x.putts))
            const minP = Math.min(...sorted.map(x => x.putts))
            const h = Math.round(20 + ((r.putts - minP) / (maxP - minP || 1)) * 60)
            const isLatest = i === sorted.length - 1
            const d = new Date(r.played_at)
            return (
              <div className="trend-bar-wrap" key={r.id}>
                <div className="trend-score-label">{r.putts}</div>
                <div className={`trend-bar ${isLatest ? 'latest' : ''}`} style={{ height: h }} />
                <div className="trend-bar-label">{MONTHS[d.getMonth()]}</div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="section-title" style={{ marginTop: 8 }}>All Rounds</div>
      <div className="rounds-list">
        {rounds.map(r => {
          const d = new Date(r.played_at)
          const { worst } = analyseRound(r)
          const wLabels = { driving: 'Driving weak', approach: 'Approach weak', shortGame: 'Short game weak', putting: 'Putting weak' }
          const holes = r.holes || 18
          return (
            <div className="round-card" key={r.id} onClick={() => navigate(`/results/${r.id}`)}>
              <div className="round-date-block">
                <div className="round-day">{d.getDate()}</div>
                <div className="round-month">{MONTHS[d.getMonth()]}</div>
              </div>
              <div className="round-info">
                <div className="round-course">{r.course}{holes === 9 ? ' (9)' : ''}</div>
                <div className="round-meta">{wLabels[worst]} · {r.fw}/{r.fw_total || 14} FW · {r.putts}p</div>
              </div>
              <div className="round-score-chip" style={{ color: scoreColor(r) }}>{scoreDiff(r)}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
